import errorLogger from '../services/errorLogger';

/**
 * Unified Error Handling Utility
 * Provides consistent error handling across the application
 */

class ErrorHandler {
  constructor() {
    this.notifications = [];
    this.listeners = new Set();
  }

  /**
   * Subscribe to error notifications
   * @param {Function} callback - Function to call when errors occur
   */
  subscribe(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  /**
   * Show error notification
   * @param {string} message - Error message
   * @param {string} type - Error type (error, warning, info)
   * @param {Object} options - Additional options
   */
  showError(message, type = 'error', options = {}) {
    const notification = {
      id: Date.now() + Math.random(),
      message,
      type,
      timestamp: new Date(),
      ...options
    };

    this.notifications.push(notification);
    this.notifyListeners(notification);

    // Auto-remove after 5 seconds for non-error types
    if (type !== 'error') {
      setTimeout(() => {
        this.removeNotification(notification.id);
      }, 5000);
    }
  }

  /**
   * Show success notification
   * @param {string} message - Success message
   */
  showSuccess(message) {
    this.showError(message, 'success');
  }

  /**
   * Show warning notification
   * @param {string} message - Warning message
   */
  showWarning(message) {
    this.showError(message, 'warning');
  }

  /**
   * Show info notification
   * @param {string} message - Info message
   */
  showInfo(message) {
    this.showError(message, 'info');
  }

  /**
   * Remove notification by ID
   * @param {string} id - Notification ID
   */
  removeNotification(id) {
    this.notifications = this.notifications.filter(n => n.id !== id);
    this.notifyListeners({ type: 'remove', id });
  }

  /**
   * Clear all notifications
   */
  clearAll() {
    this.notifications = [];
    this.notifyListeners({ type: 'clear' });
  }

  /**
   * Notify all listeners
   * @param {Object} notification - Notification object
   */
  notifyListeners(notification) {
    this.listeners.forEach(listener => {
      try {
        listener(notification);
      } catch (error) {
        errorLogger.error(error, { operation: 'notificationListener' });
      }
    });
  }

  /**
   * Get all current notifications
   * @returns {Array} Array of notifications
   */
  getNotifications() {
    return [...this.notifications];
  }

  /**
   * Handle async operation with error catching
   * @param {Function} operation - Async operation to execute
   * @param {string} errorMessage - Error message to show on failure
   * @param {Object} options - Additional options
   */
  async handleAsync(operation, errorMessage = 'An error occurred', options = {}) {
    try {
      return await operation();
    } catch (error) {
      const message = error.message || errorMessage;
      this.showError(message, 'error', options);
      throw error; // Re-throw for caller to handle if needed
    }
  }

  /**
   * Handle promise with error catching
   * @param {Promise} promise - Promise to handle
   * @param {string} errorMessage - Error message to show on failure
   * @param {Object} options - Additional options
   */
  handlePromise(promise, errorMessage = 'An error occurred', options = {}) {
    return promise.catch(error => {
      const message = error.message || errorMessage;
      this.showError(message, 'error', options);
      throw error; // Re-throw for caller to handle if needed
    });
  }
}

// Create and export singleton instance
const errorHandler = new ErrorHandler();
export default errorHandler;
